import { CarrierType } from '@planning/app/api'
import { BulkEditSelectionDto } from '@planning/hooks/useVesselVisitTableActions'
import { ContainerJourneyItemStore } from '@planning/pages/Issues/Stores/ContainerJourneyItemStore'
import { IOrderItem } from '@planning/rt-stores/order/OrderItem'
import { IVesselVisitItem } from '@planning/rt-stores/vesselVisit/VesselVisitItem'
import { VesselVisitItemStore } from '@planning/rt-stores/vesselVisit/VesselVisitItemStore'
import _ from 'lodash'
import { computed, makeObservable } from 'mobx'

export class VesselVisitBulkEditViewStore {
  constructor(
    private readonly containerJourneyItemStore: ContainerJourneyItemStore,
    private readonly vesselVisitItemStore: VesselVisitItemStore,
    private readonly vesselVisitId: number,
    private readonly bulkEditData: BulkEditSelectionDto[],
  ) {
    makeObservable(this, {
      vesselVisitItem: computed,

      containerJourneys: computed,

      inboundOrders: computed,
      outboundOrders: computed,

      inboundUnknownCount: computed,
      inboundTruckCount: computed,
      inboundTrainCount: computed,
      inboundVesselCount: computed,

      outboundUnknownCount: computed,
      outboundTruckCount: computed,
      outboundTrainCount: computed,
      outboundVesselCount: computed,

      hasAtLeastTwoDifferentInboundCarrierTypes: computed,
      hasAtLeastTwoDifferentOutboundCarrierTypes: computed,
    })
  }

  get vesselVisitItem(): IVesselVisitItem | undefined {
    return this.vesselVisitItemStore.elements[this.vesselVisitId]
  }

  get containerJourneys() {
    return _(this.containerJourneyItemStore.elements)
      .filter(cj => {
        return this.bulkEditData.some(bulk => bulk.containerJourneyId === cj.id)
      })
      .value()
  }

  get inboundOrders() {
    return this.containerJourneys.filter(cj => cj.inboundOrder).map(cj => cj.inboundOrder!)
  }

  get outboundOrders() {
    return this.containerJourneys.filter(cj => cj.outboundOrder).map(cj => cj.outboundOrder!)
  }

  get inboundUnknownCount(): number {
    return (
      this.getInboundOrdersCarrierVisitCountOfType(CarrierType.Universal) +
      this.getInboundOrdersCarrierVisitCountOfType(undefined)
    )
  }

  get inboundTruckCount(): number {
    return this.getInboundOrdersCarrierVisitCountOfType(CarrierType.Truck)
  }

  get inboundTrainCount(): number {
    return this.getInboundOrdersCarrierVisitCountOfType(CarrierType.Train)
  }

  get inboundVesselCount(): number {
    return this.getInboundOrdersCarrierVisitCountOfType(CarrierType.Vessel)
  }

  get outboundUnknownCount(): number {
    return (
      this.getOutboundOrdersCarrierVisitCountOfType(CarrierType.Universal) +
      this.getInboundOrdersCarrierVisitCountOfType(undefined)
    )
  }

  get outboundTruckCount(): number {
    return this.getOutboundOrdersCarrierVisitCountOfType(CarrierType.Truck)
  }

  get outboundTrainCount(): number {
    return this.getOutboundOrdersCarrierVisitCountOfType(CarrierType.Train)
  }

  get outboundVesselCount(): number {
    return this.getOutboundOrdersCarrierVisitCountOfType(CarrierType.Vessel)
  }

  get hasAtLeastTwoDifferentInboundCarrierTypes(): boolean {
    return this.hasAtLeastTwoDifferentCarrierTypes([
      this.inboundUnknownCount,
      this.inboundTruckCount,
      this.inboundTrainCount,
      this.inboundVesselCount,
    ])
  }

  get hasAtLeastTwoDifferentOutboundCarrierTypes(): boolean {
    return this.hasAtLeastTwoDifferentCarrierTypes([
      this.outboundUnknownCount,
      this.outboundTruckCount,
      this.outboundTrainCount,
      this.outboundVesselCount,
    ])
  }

  hasAtLeastTwoDifferentCarrierTypes(carrierTypeCounts: number[]): boolean {
    return carrierTypeCounts.filter(count => count > 0).length >= 2
  }

  getInboundOrdersCarrierVisitCountOfType(carrierType?: CarrierType): number {
    return this.getOrdersCarrierVisitCountOfType(this.inboundOrders, carrierType)
  }

  getOutboundOrdersCarrierVisitCountOfType(carrierType?: CarrierType): number {
    return this.getOrdersCarrierVisitCountOfType(this.outboundOrders, carrierType)
  }

  getOrdersCarrierVisitCountOfType(orderItems: IOrderItem[], carrierType?: CarrierType) {
    return orderItems?.filter(order => order.data.carrierVisitType === carrierType)?.length ?? 0
  }
}
